import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin } from "lucide-react";

/**
 * EventCalendar
 * - Monthly view; highlights dates that have events
 * - All times shown in Korea Standard Time (KST / UTC+9)
 * - Click a date to filter/preview events on that date
 */
export default function EventCalendar({ events = [], onSelectDate }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(null);

  // Build map: "YYYY-MM-DD" -> [events]
  // 이벤트가 endDate를 가진 경우 시작일~종료일까지 모든 날짜에 표시
  const eventMap = useMemo(() => {
    const m = new Map();
    const addDays = (dateStr, days) => {
      const [y, mo, d] = dateStr.split("-").map(Number);
      const dt = new Date(y, mo - 1, d);
      dt.setDate(dt.getDate() + days);
      const yy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${yy}-${mm}-${dd}`;
    };
    for (const e of events) {
      if (!e.date) continue;
      const startKey = e.date.slice(0, 10);
      const endKey = (e.endDate || e.date).slice(0, 10);
      // 시작일부터 종료일까지 (최대 60일 안전 제한)
      let cur = startKey;
      let safety = 0;
      while (cur <= endKey && safety < 60) {
        if (!m.has(cur)) m.set(cur, []);
        m.get(cur).push(e);
        if (cur === endKey) break;
        cur = addDays(cur, 1);
        safety += 1;
      }
    }
    return m;
  }, [events]);

  // Build weeks: 6 rows x 7 cols (Sunday-first)
  const weeks = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    // leading blanks (previous month days, dimmed)
    for (let i = 0; i < startOffset; i++) {
      const d = new Date(year, month, -startOffset + i + 1);
      cells.push({ date: d, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(year, month, d), inMonth: true });
    }
    // trailing blanks to fill 6 weeks
    while (cells.length % 7 !== 0 || cells.length < 42) {
      const last = cells[cells.length - 1].date;
      cells.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), inMonth: false });
      if (cells.length >= 42) break;
    }
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [cursor]);

  const fmtKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const selectedEvents = selected ? eventMap.get(fmtKey(selected)) || [] : [];

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="event-calendar">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100 bg-gradient-to-r from-purple-50 to-stone-50">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-purple-600 text-white flex items-center justify-center">
            <CalendarIcon className="w-4 h-4" />
          </div>
          <div>
            <p className="text-base font-bold text-stone-800">
              {cursor.getFullYear()}년 {cursor.getMonth() + 1}월
            </p>
            <p className="text-[11px] text-stone-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              모든 일정은 <span className="font-semibold text-purple-700">한국시간(KST · UTC+9)</span> 기준
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="w-9 h-9 rounded-full bg-white border border-stone-200 hover:bg-stone-50 flex items-center justify-center"
            aria-label="이전 달"
            data-testid="cal-prev"
          >
            <ChevronLeft className="w-4 h-4 text-stone-600" />
          </button>
          <button
            type="button"
            onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
            className="px-3 h-9 text-xs font-medium rounded-full bg-white border border-stone-200 hover:bg-stone-50 text-stone-600"
            data-testid="cal-today"
          >
            오늘
          </button>
          <button
            type="button"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="w-9 h-9 rounded-full bg-white border border-stone-200 hover:bg-stone-50 flex items-center justify-center"
            aria-label="다음 달"
            data-testid="cal-next"
          >
            <ChevronRight className="w-4 h-4 text-stone-600" />
          </button>
        </div>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 px-3 pt-3 pb-1 text-center text-[11px] font-semibold tracking-wide">
        {["일", "월", "화", "수", "목", "금", "토"].map((d, i) => (
          <div
            key={d}
            className={`py-1 ${i === 0 ? "text-red-500" : i === 6 ? "text-blue-500" : "text-stone-500"}`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1 px-3 pb-3">
        {weeks.flat().map((cell, idx) => {
          const key = fmtKey(cell.date);
          const dayEvents = eventMap.get(key) || [];
          const has = dayEvents.length > 0;
          const isToday = isSameDay(cell.date, today);
          const isSelected = selected && isSameDay(cell.date, selected);
          const dayNum = cell.date.getDate();
          const dow = cell.date.getDay();

          return (
            <button
              key={idx}
              type="button"
              onClick={() => {
                if (!has) return;
                if (isSelected) {
                  setSelected(null);
                  onSelectDate && onSelectDate(null);
                } else {
                  setSelected(cell.date);
                  onSelectDate && onSelectDate(key);
                }
              }}
              disabled={!has}
              className={`relative h-12 md:h-14 rounded-xl text-sm flex flex-col items-center justify-center transition-all ${
                !cell.inMonth ? "text-stone-300" :
                isSelected ? "bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-lg ring-2 ring-purple-300" :
                has ? "bg-gradient-to-br from-purple-100 to-pink-100 hover:from-purple-200 hover:to-pink-200 text-purple-900 cursor-pointer shadow-sm ring-1 ring-purple-200" :
                "text-stone-500 hover:bg-stone-50"
              } ${isToday && !isSelected ? "ring-2 ring-purple-400" : ""}`}
              data-testid={`cal-day-${key}`}
            >
              <span className={`font-bold ${
                isSelected ? "text-white" :
                has ? "text-purple-700" :
                dow === 0 && cell.inMonth ? "text-red-500" :
                dow === 6 && cell.inMonth ? "text-blue-500" :
                ""
              }`}>{dayNum}</span>
              {has && (
                <div className="flex items-center gap-0.5 mt-0.5">
                  {dayEvents.slice(0, 3).map((_, i) => (
                    <span
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-purple-600"}`}
                    />
                  ))}
                  {dayEvents.length > 3 && (
                    <span className={`text-[9px] font-bold ml-0.5 ${isSelected ? "text-white" : "text-purple-700"}`}>
                      +{dayEvents.length - 3}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected day details */}
      {selected && selectedEvents.length > 0 && (
        <div className="border-t border-stone-100 px-5 py-4 bg-stone-50">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-stone-800">
              {selected.getMonth() + 1}월 {selected.getDate()}일 일정 ({selectedEvents.length}건)
            </p>
            <button
              onClick={() => { setSelected(null); onSelectDate && onSelectDate(null); }}
              className="text-xs text-stone-500 hover:text-stone-700"
              data-testid="cal-clear-selection"
            >
              전체 보기
            </button>
          </div>
          <div className="space-y-2">
            {selectedEvents.map((e) => (
              <div
                key={e.id}
                className="flex items-center gap-3 p-2.5 bg-white rounded-xl border border-stone-200"
                data-testid={`cal-event-${e.id}`}
              >
                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-stone-100">
                  {e.image && <img src={e.image} alt={e.title} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-stone-800 truncate">{e.title}</p>
                  <div className="flex items-center gap-3 text-[11px] text-stone-500 mt-0.5">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {e.time} KST
                    </span>
                    {e.location && (
                      <span className="flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{e.location}</span>
                      </span>
                    )}
                  </div>
                </div>
                {e.price ? (
                  <p className="text-sm font-bold text-purple-600 shrink-0">
                    ₩{new Intl.NumberFormat("ko-KR").format(e.price)}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
